import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Stack, TextColors, Typography } from '@sureco/design-system'
import {
  Button,
  Card,
  Input,
  Modal,
  Table,
  Tooltip,
} from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import { CopyableValue } from 'components/CopyableValue'
import RHFInput from 'enrollment-platform/components/form/hookform/Input/RHFInput'
import RHFSelect from 'enrollment-platform/components/form/hookform/Select/RHFSelect'
import React, { useCallback, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import type { AgentCarrierIdentifier } from 'enrollment-platform/api/partner-benefit-graph/ideon-agents.types'
import {
  CARRIER_INTEGRATIONS,
  carrierIntegrationOptions,
  formatCarrierIntegrationLabel,
} from '../carrier-integrations'
import { formatStateLabel, stateOptions } from './shared'
import { useTablePagination } from './useTablePagination'

const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
}

type CarrierFormValues = {
  carrierIntegration: string
  state: string
  identifier: string
}

const emptyCarrier: CarrierFormValues = {
  carrierIntegration: '',
  state: '',
  identifier: '',
}

export type CarrierIdentifiersCardProps = {
  loading: boolean
  carrierIdentifiers: AgentCarrierIdentifier[]
  addCarrierIdentifier: (body: {
    carrierIntegration: string
    state: string
    identifier: string
  }) => Promise<boolean>
  editCarrierIdentifier: (
    id: string,
    body: {
      state: string
      identifier: string
    }
  ) => Promise<boolean>
  removeCarrierIdentifier: (id: string) => Promise<boolean>
}

export default function CarrierIdentifiersCard({
  loading,
  carrierIdentifiers,
  addCarrierIdentifier,
  editCarrierIdentifier,
  removeCarrierIdentifier,
}: CarrierIdentifiersCardProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<AgentCarrierIdentifier | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [search, setSearch] = useState('')

  const filteredIdentifiers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return carrierIdentifiers
    return carrierIdentifiers.filter((c) =>
      [
        c.id,
        c.carrierIntegration,
        formatCarrierIntegrationLabel(c.carrierIntegration),
        c.state,
        formatStateLabel(c.state),
        c.identifier,
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    )
  }, [carrierIdentifiers, search])

  const { paginationProps, resetToFirstPage } = useTablePagination(
    filteredIdentifiers.length
  )

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value)
      // Reset to page 1 so the user doesn't land on an empty page when the
      // filtered set shrinks.
      resetToFirstPage()
    },
    [resetToFirstPage]
  )

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm<CarrierFormValues>({
    mode: 'onChange',
    defaultValues: emptyCarrier,
  })

  const watchedCarrier = useWatch({ control, name: 'carrierIntegration' })
  const watchedState = useWatch({ control, name: 'state' })

  const takenStatesByCarrier = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const c of carrierIdentifiers) {
      if (c.id === editing?.id) continue
      let states = map.get(c.carrierIntegration)
      if (!states) {
        states = new Set()
        map.set(c.carrierIntegration, states)
      }
      states.add(c.state)
    }
    return map
  }, [carrierIdentifiers, editing])

  const stateOptionsForSelect = useMemo(() => {
    const taken = watchedCarrier
      ? (takenStatesByCarrier.get(watchedCarrier) ?? new Set<string>())
      : new Set<string>()
    return stateOptions.map((o) => ({ ...o, disabled: taken.has(o.value) }))
  }, [watchedCarrier, takenStatesByCarrier])

  // Guards the case where state is chosen first under one carrier, then the
  // carrier is switched to one that already has that state — the dropdown
  // disables future picks, but the existing form value stays selected.
  const isComboTaken = useMemo(() => {
    if (!watchedCarrier || !watchedState) return false
    return takenStatesByCarrier.get(watchedCarrier)?.has(watchedState) ?? false
  }, [watchedCarrier, watchedState, takenStatesByCarrier])

  const canAdd = useMemo(() => {
    const taken = new Set(
      carrierIdentifiers.map((c) => `${c.carrierIntegration}:${c.state}`)
    )
    const total = stateOptions.length * CARRIER_INTEGRATIONS.length
    return taken.size < total
  }, [carrierIdentifiers])

  const openAdd = useCallback(() => {
    setEditing(null)
    reset(emptyCarrier)
    setModalOpen(true)
  }, [reset])

  const openEdit = useCallback(
    (row: AgentCarrierIdentifier) => {
      setEditing(row)
      reset({
        carrierIntegration: row.carrierIntegration,
        state: row.state,
        identifier: row.identifier,
      })
      setModalOpen(true)
    },
    [reset]
  )

  const submitModal = handleSubmit(async (v) => {
    if (submitting) return
    if (takenStatesByCarrier.get(v.carrierIntegration)?.has(v.state)) {
      // Defence in depth — the OK button is disabled in this state, but
      // Enter-to-submit can race the React state update.
      return
    }
    setSubmitting(true)
    try {
      const ok = editing?.id
        ? await editCarrierIdentifier(editing.id, {
            state: v.state,
            identifier: v.identifier,
          })
        : await addCarrierIdentifier({
            carrierIntegration: v.carrierIntegration,
            state: v.state,
            identifier: v.identifier,
          })
      if (ok) {
        setModalOpen(false)
        reset(emptyCarrier)
      }
    } finally {
      setSubmitting(false)
    }
  })

  const confirmDelete = useCallback(
    (row: AgentCarrierIdentifier) => {
      Modal.confirm({
        title: 'Delete carrier identifier?',
        content: `Remove the carrier identifier "${row.identifier}"?`,
        okText: 'Delete',
        okType: 'danger',
        // Reject so Ant keeps the dialog open when the request fails — the
        // error toast appears, the user can retry or cancel.
        onOk: async () => {
          const ok = await removeCarrierIdentifier(row.id)
          if (!ok) return Promise.reject()
        },
      })
    },
    [removeCarrierIdentifier]
  )

  const columns = useMemo<ColumnsType<AgentCarrierIdentifier>>(
    () => [
      {
        title: 'Carrier',
        dataIndex: 'carrierIntegration',
        key: 'carrierIntegration',
        render: (code?: string) => formatCarrierIntegrationLabel(code),
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        render: (code?: string) => formatStateLabel(code),
      },
      {
        title: 'Identifier',
        dataIndex: 'identifier',
        key: 'identifier',
        render: (identifier?: string) => <CopyableValue value={identifier} />,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_: unknown, row: AgentCarrierIdentifier) => (
          <Stack direction="row" space={8}>
            <Tooltip title="Edit">
              <Button
                type="default"
                icon={<EditOutlined aria-hidden="true" />}
                onClick={() => openEdit(row)}
                aria-label="Edit carrier identifier"
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                type="default"
                danger
                icon={<DeleteOutlined aria-hidden="true" />}
                onClick={() => confirmDelete(row)}
                aria-label="Delete carrier identifier"
              />
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [confirmDelete, openEdit]
  )

  return (
    <>
      <Card
        title="Carrier Identifiers"
        bordered={false}
        extra={
          <Stack
            direction="row"
            space={8}
            wrap="wrap"
            justify="flex-end"
            align="center"
          >
            <Input
              prefix={<SearchOutlined aria-hidden="true" />}
              placeholder="Search carrier identifiers"
              value={search}
              onChange={onSearchChange}
              allowClear
              aria-label="Search carrier identifiers"
              // Fixed comfortable width on wide viewports; shrinks with the
              // header slot on narrow viewports so it can wrap above the Add
              // button instead of overflowing. fontSize: 16 prevents iOS
              // Safari from zooming the viewport on focus.
              style={{ width: 240, maxWidth: '100%', fontSize: 16 }}
            />
            <Tooltip
              title={
                canAdd
                  ? ''
                  : 'Every carrier/state combination is already configured.'
              }
              trigger={['hover', 'focus']}
            >
              {/* Ant disabled buttons swallow pointer events, so the tooltip
                  wouldn't fire on them — the span wrapper receives hover. */}
              <span style={{ display: 'inline-flex' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined aria-hidden="true" />}
                  onClick={openAdd}
                  disabled={!canAdd}
                >
                  Add
                </Button>
              </span>
            </Tooltip>
          </Stack>
        }
      >
        <>
          {/* Visually hidden live region so screen readers announce filter
              results as the user types. */}
          <div role="status" aria-live="polite" style={srOnlyStyle}>
            {search.trim()
              ? `${filteredIdentifiers.length} of ${carrierIdentifiers.length} carrier identifiers match`
              : ''}
          </div>
          <Table
            rowKey={(r) => r.id}
            loading={loading}
            dataSource={filteredIdentifiers}
            columns={columns}
            pagination={paginationProps}
            locale={{
              emptyText: search.trim()
                ? 'No carrier identifiers match your search'
                : 'No carrier identifiers',
            }}
            scroll={{ x: 'max-content' }}
          />
        </>
      </Card>

      <Modal
        title={editing ? 'Edit carrier identifier' : 'Add carrier identifier'}
        open={modalOpen}
        onOk={() => void submitModal()}
        confirmLoading={submitting}
        okButtonProps={{
          disabled: !isValid || isComboTaken || (editing != null && !isDirty),
        }}
        cancelButtonProps={{ disabled: submitting }}
        onCancel={() => {
          if (submitting) return
          setModalOpen(false)
          reset(emptyCarrier)
        }}
        maskClosable={!submitting}
        destroyOnClose
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void submitModal()
          }}
          noValidate
        >
          <Stack direction="col" space={12}>
            <Stack>
              <Typography size={14}>Carrier</Typography>
              <RHFSelect
                name="carrierIntegration"
                control={control}
                rules={{ required: 'Required' }}
                showSearch
                optionFilterProp="label"
                placeholder="Select carrier"
                options={carrierIntegrationOptions}
                aria-label="Carrier"
                aria-required="true"
                // The update API only accepts `state` and `identifier`, so a
                // change here would be silently dropped on save. Lock the
                // field on edit to make that constraint visible.
                disabled={editing != null}
              />
            </Stack>
            <Stack>
              <Typography size={14}>State</Typography>
              <RHFSelect
                name="state"
                control={control}
                rules={{ required: 'Required' }}
                showSearch
                optionFilterProp="label"
                placeholder="Select state"
                options={stateOptionsForSelect}
                aria-label="State"
                aria-required="true"
              />
              {isComboTaken ? (
                // role="alert" so screen readers announce the error when it
                // appears (e.g., user switches carrier to one where the
                // already-selected state is taken).
                <div role="alert">
                  <Typography size={12} color={TextColors.error}>
                    This carrier and state combination is already configured.
                  </Typography>
                </div>
              ) : null}
            </Stack>
            <Stack>
              <Typography size={14}>Identifier</Typography>
              <RHFInput
                name="identifier"
                control={control}
                rules={{ required: 'Required' }}
                aria-label="Identifier"
                aria-required="true"
              />
            </Stack>
          </Stack>
          {/* Enables Enter-to-submit; Ant's Modal OK button sits outside this form */}
          <button type="submit" hidden aria-hidden tabIndex={-1} />
        </form>
      </Modal>
    </>
  )
}
